import { useCallback, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _ from "lodash"
import { useVenue } from "providers/venue"
import { getFeatureIDByPath } from "utils/feature"
import { DEFAULT_VENUE_FILTER, Q_VENUE } from "constant"
import { useGetServices } from "providers/venue/modules/data/services/useGetServices"
import { useGeoLocation } from "providers/geolocation"
import { useVenueController } from "hooks/venue"
import { canShowOnDevice } from "./utils"
import config from "config"

const device = config("device")

export const useServiceDetail = (serviceId: string) => {
  const navigate = useNavigate()
  const { findFeatureById } = useVenue()
  const [searchParams] = useSearchParams()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const { data: services = [] } = useGetServices()
  const service = useMemo(
    () => services.find((service) => service?.id === serviceId),
    [services, serviceId]
  )
  const serviceFilteredByVenue = useMemo(() => {
    if (!canShowOnDevice(service?.target_device, device)) return null
    const isAllVenue = venueFilter === DEFAULT_VENUE_FILTER
    const serviceVenueId = service.venue_id
    // Return null if the service is tied to a specific venue that doesn't match the current venue filter — to be filtered out later.
    if (!isAllVenue && serviceVenueId && serviceVenueId !== venueFilter)
      return null

    let filteredLocation = service.locations

    if (!isAllVenue) {
      filteredLocation = filteredLocation.filter((location) => {
        const featureId = getFeatureIDByPath(location.url)
        const feature = findFeatureById(featureId)

        // Keep locations that do not use a feature ID.
        if (!feature) return true

        // Filter locations by the specified venue filter ID.
        return feature?.properties?.venue_id === venueFilter
      })

      // Return null if no service locations match the venue filter — will be excluded in the next step.
      if (filteredLocation.length === 0) return null
    }

    return {
      ...service,
      locations: filteredLocation,
    }
  }, [venueFilter, findFeatureById, service])

  const hasData = _.size(serviceFilteredByVenue) > 0

  const changeVenueFilter = useCallback(
    (venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)
      navigate(`/services/${serviceId}?${searchParams.toString()}`, {
        replace: true,
      })
    },
    [navigate, searchParams, serviceId]
  )

  return {
    hasData,
    service,
    serviceFilteredByVenue,
    changeVenueFilter,
    venueFilter,
  }
}
